import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  BarChart3,
  CalendarClock,
  Camera,
  Contact,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  MapPin,
  Mic,
  Paperclip,
  Smile,
  Pencil,
  Play,
  Plus,
  CornerUpLeft,
  SendHorizontal,
  Share2,
  SquareKanban,
  Sticker,
  Trash2,
  Type,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { useCurrentLocation } from "@/hooks/useCurrentLocation";
import { useLocale } from "@/hooks/useLocale";
import { formatBytes } from "@/utils/file.utils";
import { useGetChatContactsInfiniteQuery } from "@/services/(my-space)/api.chat";
import { cn } from "@/utils/cn";
import {
  CHAT_MAX_ATTACHMENTS,
  CHAT_MAX_TEXT_LENGTH,
  type IChatContact,
  type IChatMember,
  type IChatMention,
  type IChatMessage,
  type IChatOutgoingMessage,
} from "@/types/(my-space)/chat.interface";
import {
  durationLabel,
  messageContentPreview,
  personName,
  scheduledTimeLabel,
} from "../_lib/chat-labels";
import { insertAtSelection } from "../_lib/emoji-text";
import { EmojiPicker } from "./EmojiPicker";
import { recordEmojiUse } from "../_lib/emoji-usage";
import { FormatToolbar } from "./FormatToolbar";
import {
  applyFormat,
  formatForShortcut,
  insideOpenFence,
  type FormatKind,
} from "../_lib/markdown";
import { fileKind, messageTypeForMedia } from "../_lib/chat-utils";
import {
  filterMentionCandidates,
  findMentionQuery,
  insertMention,
  mentionIdsToSend,
  mentionName,
} from "../_lib/mentions";
import { compressVideo } from "../_lib/compress-video";
import { sampleLevels, toWaveform } from "../_lib/waveform";
import { VOICE_PREFERRED_MIME_TYPES, isMp4Audio, toM4a } from "../_lib/voice-format";
import type { ChatRoomState } from "../_hooks/useChatRoom";
import { ChatAvatar } from "./ChatAvatar";
import { CameraDialog, LinkDialog, PollDialog, ShortcutDialog, TicketDialog } from "./ComposerDialogs";
import { PeoplePickerDialog } from "./PeoplePickerDialog";
import { ScheduleDialog } from "./ScheduleDialog";

export interface ComposerHandle {
  addFiles: (files: File[]) => void;
  startReply: (m: IChatMessage) => void;
  startEdit: (m: IChatMessage) => void;
}

interface ComposerProps {
  room: ChatRoomState;
  /** A floating window: tight spacing, so the text gets the width. */
  compact?: boolean;
}

interface Draft {
  id: string;
  file: File;
  /** Picked with "File": sent as a document (download row), never inline. */
  asDocument: boolean;
  previewUrl: string | null;
}

/**
 * How picked files enter the tray: "document" from the File option; "auto"
 * (gallery, camera, GIF, drop, paste) makes photos and videos media and
 * anything else a document.
 */
type AddMode = "auto" | "document";

type Sheet = "camera" | "poll" | "link" | "shortcut" | "ticket" | "contact" | null;

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  { room, compact = false },
  ref,
) {
  // In a narrow window, the buttons and gaps would leave the text a sliver.
  const iconBtn = compact ? "size-7" : undefined;
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const [text, setText] = useState("");
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [replyTo, setReplyTo] = useState<IChatMessage | null>(null);
  const [editing, setEditing] = useState<IChatMessage | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const gifInput = useRef<HTMLInputElement>(null);
  const recorder = useVoiceRecorder();
  const [recordStart, setRecordStart] = useState<number | null>(null);
  // Loudness while recording: the live meter now, the note's waveform after.
  const levelsRef = useRef<ReturnType<typeof sampleLevels> | null>(null);
  // The mic stream is state, so it arrives a render after start() resolves.
  useEffect(() => {
    if (!recorder.stream) return;
    levelsRef.current?.stop();
    levelsRef.current = sampleLevels(recorder.stream);
  }, [recorder.stream]);
  // Waiting for the browser's microphone prompt — show it, or the click looks dead.
  const [startingRecording, setStartingRecording] = useState(false);
  const [now, setNow] = useState(Date.now());
  const { locate, isLocating } = useCurrentLocation();

  const conversationId = room.detail?.id;

  // ── Mentions ─────────────────────────────────────────────────────────────
  // People picked from the suggestions; what's sent is recomputed from the
  // text at send time, so a name edited or deleted since drops its tag.
  const [tagged, setTagged] = useState<IChatMention[]>([]);
  const [caret, setCaret] = useState(0);
  // Escape closes the suggestions for this "@" until another one is typed.
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [activeMention, setActiveMention] = useState(0);
  const mentionListId = useId();
  const chatType = room.detail?.type;
  // A direct chat tags anyone in the company: they aren't in it, so they're
  // never told (the server only notifies members). Groups tag their members.
  const isDirectChat = chatType === "direct";
  const canMention = !editing && (chatType === "group" || chatType === "broadcast" || isDirectChat);
  const mentionToken = canMention ? findMentionQuery(text, caret) : null;
  const mentionQuery = mentionToken?.query ?? null;
  const members = room.detail?.members;
  const directory = useGetChatContactsInfiniteQuery(
    { search: mentionQuery ?? "" },
    { skip: !isDirectChat || mentionQuery == null },
  );
  const candidates = useMemo((): IChatMember[] => {
    if (mentionQuery == null) return [];
    if (!isDirectChat) return filterMentionCandidates(members ?? [], mentionQuery, room.myUserId);
    return (directory.data?.pages[0]?.data ?? [])
      .filter((u) => u.id !== room.myUserId)
      .slice(0, 8)
      .map((u) => ({ ...u, role: "member" }) as IChatMember);
  }, [directory.data, isDirectChat, members, mentionQuery, room.myUserId]);
  const suggesting =
    mentionToken != null && mentionToken.start !== dismissedAt && candidates.length > 0;
  useEffect(() => setActiveMention(0), [mentionQuery]);
  const noMentionToken = mentionToken == null;
  useEffect(() => {
    if (noMentionToken) setDismissedAt(null);
  }, [noMentionToken]);

  // A new conversation starts with an empty composer.
  useEffect(() => {
    setText("");
    setTagged([]);
    setReplyTo(null);
    setEditing(null);
    setDrafts((list) => {
      list.forEach((d) => d.previewUrl && URL.revokeObjectURL(d.previewUrl));
      return [];
    });
  }, [conversationId]);

  useEffect(
    () => () => drafts.forEach((d) => d.previewUrl && URL.revokeObjectURL(d.previewUrl)),
    // Revoke on unmount only; removals revoke their own.
    [],
  );

  const addFiles = (files: File[], mode: AddMode = "auto") => {
    if (!files.length) return;
    setDrafts((list) => {
      const known = new Set(list.map((d) => `${d.file.name}:${d.file.size}`));
      const fresh = files.filter((f) => !known.has(`${f.name}:${f.size}`));
      const room = CHAT_MAX_ATTACHMENTS - list.length;
      if (fresh.length > room) {
        toast.error(t("composer.max_attachments", { count: CHAT_MAX_ATTACHMENTS }));
      }
      const added = fresh.slice(0, Math.max(room, 0)).map((file) => {
        const kind = fileKind(file);
        const asDocument = mode === "document" || (kind !== "image" && kind !== "video");
        return {
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          asDocument,
          // Media get a thumbnail; documents always show as a file tile.
          previewUrl: !asDocument ? URL.createObjectURL(file) : null,
        };
      });
      return [...list, ...added];
    });
    setEditing(null);
    textareaRef.current?.focus();
  };

  const removeDraft = (id: string) =>
    setDrafts((list) => {
      const d = list.find((x) => x.id === id);
      if (d?.previewUrl) URL.revokeObjectURL(d.previewUrl);
      return list.filter((x) => x.id !== id);
    });

  useImperativeHandle(ref, () => ({
    addFiles: (files) => addFiles(files),
    startReply: (m) => {
      setEditing(null);
      setReplyTo(m);
      textareaRef.current?.focus();
    },
    startEdit: (m) => {
      setReplyTo(null);
      setDrafts([]);
      setEditing(m);
      setText(m.body ?? "");
      requestAnimationFrame(() => textareaRef.current?.focus());
    },
  }));

  const onTextChange = (value: string) => {
    setText(value);
    if (!value) setTagged([]);
    if (!editing) room.onInputChanged(value);
  };

  const [emojiOpen, setEmojiOpen] = useState(false);

  /** Types an emoji over the selection (at the caret) and keeps typing there. */
  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    const next = insertAtSelection(text, start, end, emoji);
    if (next.text.length > CHAT_MAX_TEXT_LENGTH) return;
    onTextChange(next.text);
    setCaret(next.caret);
    recordEmojiUse(room.myUserId, emoji);
    requestAnimationFrame(() => el?.setSelectionRange(next.caret, next.caret));
  };

  /** The Markdown bar (Aa): off until asked for, like Teams. */
  const [formatOpen, setFormatOpen] = useState(false);

  /** A toolbar button or shortcut: formats the selection, keeps it selected. */
  const formatSelection = (kind: FormatKind) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    const next = applyFormat(kind, text, start, end, (k) => t(`format.placeholder.${k}`));
    if (next.text.length > CHAT_MAX_TEXT_LENGTH) return;
    onTextChange(next.text);
    setCaret(next.selectionEnd);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  };

  /** Swaps the "@query" at the caret for "@Full Name " and remembers who. */
  const pickMention = (member: IChatMember) => {
    if (!mentionToken) return;
    const name = mentionName(member);
    const next = insertMention(text, mentionToken, caret, name);
    setTagged((list) => [...list.filter((m) => m.userId !== member.id), { userId: member.id, name }]);
    room.rememberMention({ userId: member.id, name });
    onTextChange(next.text);
    setCaret(next.caret);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      el?.focus();
      el?.setSelectionRange(next.caret, next.caret);
    });
  };

  const [compressing, setCompressing] = useState<number | null>(null);

  /** Videos re-encoded smaller before upload; photos untouched. */
  const compressMedia = async (files: File[]) => {
    const out: File[] = [];
    for (const f of files) {
      if (fileKind(f) !== "video") {
        out.push(f);
        continue;
      }
      setCompressing(0);
      out.push(await compressVideo(f, (p) => setCompressing(Math.round(p * 100))));
    }
    setCompressing(null);
    return out;
  };

  const submit = async () => {
    const body = text.trim();
    // Tags whose "@Full Name" survived to the send (captions too).
    const mentions = mentionIdsToSend(body, tagged);
    const sentTags = tagged;
    if (editing) {
      if (!body) return;
      const target = editing;
      setEditing(null);
      setText("");
      if (body !== target.body) await room.edit(target, body);
      return;
    }
    if (drafts.length) {
      const media = drafts.filter((d) => !d.asDocument);
      const documents = drafts.filter((d) => d.asDocument);
      const reply = replyTo;
      drafts.forEach((d) => d.previewUrl && URL.revokeObjectURL(d.previewUrl));
      setDrafts([]);
      setText("");
      setTagged([]);
      setReplyTo(null);
      room.stopTyping();
      const failed: Draft[] = [];
      let captionSent = false;

      // Photos and videos: one inline message, videos compressed first.
      if (media.length) {
        const files = await compressMedia(media.map((d) => d.file));
        const ok = await room.uploadAndSend(messageTypeForMedia(files), files, {
          caption: body,
          mentions,
          replyTo: reply,
        });
        if (ok) captionSent = true;
        else failed.push(...media);
      }
      // Documents: their own message of download rows, sent as picked.
      if (documents.length) {
        const ok = await room.uploadAndSend(
          "file",
          documents.map((d) => d.file),
          {
            caption: captionSent ? undefined : body,
            mentions: captionSent ? undefined : mentions,
            replyTo: media.length ? null : reply,
          },
        );
        if (ok) captionSent = true;
        else failed.push(...documents);
      }
      // Anything that didn't go out comes back, so nothing picked or typed is lost.
      if (failed.length) {
        failed.forEach((d) => addFiles([d.file], d.asDocument ? "document" : "auto"));
        if (!captionSent) {
          setText(body);
          setTagged(sentTags);
        }
      }
      return;
    }
    if (!body) return;
    const reply = replyTo;
    setText("");
    setTagged([]);
    setReplyTo(null);
    room.stopTyping();
    await room.send({ type: "text", body, mentions }, { replyTo: reply });
  };

  /**
   * Schedules what's typed and staged, split exactly as a send splits it
   * (media with the caption, documents after). Unlike a send nothing is
   * cleared up front: whatever couldn't be scheduled stays in the composer.
   */
  const scheduleDraft = async (sendAt: Date) => {
    const body = text.trim();
    const mentions = mentionIdsToSend(body, tagged);
    const reply = replyTo;
    setScheduling(true);
    try {
      let allDone = true;
      let anyDone = false;
      if (!drafts.length) {
        if (!body) return;
        allDone = await room.schedule(
          { type: "text", body, mentions },
          { sendAt, replyTo: reply },
        );
        anyDone = allDone;
      } else {
        const media = drafts.filter((d) => !d.asDocument);
        const documents = drafts.filter((d) => d.asDocument);
        const done: Draft[] = [];
        if (media.length) {
          const files = await compressMedia(media.map((d) => d.file));
          const ok = await room.schedule(
            { type: messageTypeForMedia(files), body: body || undefined, mentions },
            { sendAt, replyTo: reply, files },
          );
          if (ok) done.push(...media);
          else allDone = false;
        }
        if (allDone && documents.length) {
          const ok = await room.schedule(
            {
              type: "file",
              body: (done.length ? undefined : body) || undefined,
              mentions: done.length ? undefined : mentions,
            },
            { sendAt, replyTo: media.length ? null : reply, files: documents.map((d) => d.file) },
          );
          if (ok) done.push(...documents);
          else allDone = false;
        }
        anyDone = done.length > 0;
        if (anyDone) {
          done.forEach((d) => d.previewUrl && URL.revokeObjectURL(d.previewUrl));
          setDrafts((list) => list.filter((d) => !done.includes(d)));
        }
      }
      if (anyDone) {
        // The caption and reply went with the first message that was scheduled.
        setText("");
        setTagged([]);
        setReplyTo(null);
        room.stopTyping();
        toast.success(
          t("scheduled.scheduled_for", { time: scheduledTimeLabel(t, formatDate, sendAt) }),
        );
      }
      if (allDone) setScheduleOpen(false);
    } finally {
      setScheduling(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggesting && !e.nativeEvent.isComposing) {
      const count = candidates.length;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setActiveMention((i) => (i + (e.key === "ArrowDown" ? 1 : count - 1)) % count);
        return;
      }
      if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") {
        e.preventDefault();
        const pick = candidates[activeMention] ?? candidates[0];
        if (pick) pickMention(pick);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setDismissedAt(mentionToken?.start ?? null);
        return;
      }
    }
    const shortcut = formatForShortcut(e);
    if (shortcut) {
      e.preventDefault();
      formatSelection(shortcut);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      // Inside an unclosed ``` block Enter is a new line of code.
      if (insideOpenFence(text, e.currentTarget.selectionStart ?? text.length)) return;
      e.preventDefault();
      void submit();
    }
    if (e.key === "Escape" && (editing || replyTo)) {
      e.preventDefault();
      if (editing) setText("");
      setEditing(null);
      setReplyTo(null);
    }
  };

  const sendOther = async (message: IChatOutgoingMessage, metadata?: IChatMessage["metadata"]) => {
    const reply = replyTo;
    setReplyTo(null);
    await room.send(message, { replyTo: reply, localMetadata: metadata ?? null });
  };

  const shareLocation = async () => {
    setAttachOpen(false);
    const fix = await locate();
    if (!fix) {
      toast.error(t("composer.location_unavailable"));
      return;
    }
    const location = { latitude: fix.lat, longitude: fix.long };
    await sendOther({ type: "location", location }, { location });
  };

  const shareContact = async (contact: IChatContact) => {
    const name = personName(t, contact);
    await sendOther(
      { type: "contact", contact: { userId: contact.id } },
      { contact: { userId: contact.id, name, email: contact.email ?? undefined, profileImage: contact.profileImage } },
    );
  };

  // ── Voice ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (recordStart == null) return;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [recordStart]);

  // The others see "recording…" for exactly as long as the recorder runs
  // (started, sent, cancelled or timed out).
  const { setRecording } = room;
  useEffect(() => setRecording(recordStart != null), [recordStart, setRecording]);

  const startRecording = async () => {
    if (startingRecording) return;
    setStartingRecording(true);
    try {
      await recorder.start({
        onTimeout: () => setRecordStart(null),
        // MP4/AAC first: the phones (iOS especially) can't play WebM/Opus.
        preferMimeTypes: VOICE_PREFERRED_MIME_TYPES,
      });
      setRecordStart(Date.now());
      setNow(Date.now());
    } catch {
      toast.error(t("composer.mic_permission"));
    } finally {
      setStartingRecording(false);
    }
  };

  const cancelRecording = () => {
    levelsRef.current?.stop();
    levelsRef.current = null;
    recorder.cancel();
    setRecordStart(null);
  };

  const finishRecording = async () => {
    const startedAt = recordStart;
    setRecordStart(null);
    const waveform = toWaveform(levelsRef.current?.stop() ?? []);
    levelsRef.current = null;
    try {
      // Still WebM/Ogg (Firefox, older Chrome): re-encode so phones can play it.
      const blob = await toM4a(await recorder.stop());
      const durationMs = startedAt ? Date.now() - startedAt : undefined;
      if (!blob.size) return;
      const ext = isMp4Audio(blob.type) ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
      // Bare type: browsers add parameters ("audio/webm;codecs=opus").
      const type = blob.type.split(";")[0] || "audio/webm";
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type });
      const reply = replyTo;
      setReplyTo(null);
      await room.uploadAndSend("voice", [file], { durationMs, waveform, replyTo: reply });
    } catch {
      // Recorder already stopped (timeout) — nothing to send.
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const attachItems = useMemo(
    () => [
      { key: "gallery", icon: ImageIcon, tone: "bg-info-muted text-info", label: t("composer.gallery"), run: () => galleryInput.current?.click() },
      { key: "camera", icon: Camera, tone: "bg-danger-muted text-danger", label: t("composer.camera"), run: () => setSheet("camera") },
      { key: "file", icon: FileText, tone: "bg-primary-muted text-primary", label: t("composer.file"), run: () => fileInput.current?.click() },
      { key: "gif", icon: Sticker, tone: "bg-accent-muted text-accent-foreground", label: t("composer.gif"), run: () => gifInput.current?.click() },
      { key: "location", icon: MapPin, tone: "bg-success-muted text-success", label: t("composer.location"), run: () => void shareLocation() },
      { key: "contact", icon: Contact, tone: "bg-warning-muted text-warning", label: t("composer.contact"), run: () => setSheet("contact") },
      { key: "poll", icon: BarChart3, tone: "bg-info-muted text-info", label: t("composer.poll"), run: () => setSheet("poll") },
      { key: "shortcut", icon: Share2, tone: "bg-primary-muted text-primary", label: t("composer.shortcut"), run: () => setSheet("shortcut") },
      { key: "ticket", icon: SquareKanban, tone: "bg-info-muted text-info", label: t("composer.ticket"), run: () => setSheet("ticket") },
      { key: "link", icon: Link2, tone: "bg-success-muted text-success", label: t("composer.link"), run: () => setSheet("link") },
    ],
    [t, replyTo],
  );

  const recording = recordStart != null;
  const canSubmit = !!text.trim() || drafts.length > 0;
  // Typed text and/or staged files; never an edit (voice has its own bar).
  const canSchedule = canSubmit && !editing;
  const busy = room.isUploading || compressing != null || scheduling;

  // Nothing left to schedule (sent, cleared, another chat): the dialog goes too.
  useEffect(() => {
    if (!canSchedule) setScheduleOpen(false);
  }, [canSchedule]);

  return (
    <div
      className={cn(
        "border-t border-border bg-background",
        // A window sits on a wide page: md: padding would apply there too.
        compact ? "px-2 pt-1.5 pb-2" : "px-3 pt-2 pb-3 md:px-5",
      )}
    >
      {replyTo || editing ? (
        <div className="mb-2 flex items-center gap-2 rounded-md border-s-4 border-primary bg-muted px-3 py-1.5">
          {editing ? (
            <Pencil className="size-4 shrink-0 text-primary" />
          ) : (
            <CornerUpLeft className="size-4 shrink-0 text-primary rtl-flip" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-primary">
              {editing
                ? t("composer.editing")
                : replyTo!.sender?.id === room.myUserId
                  ? t("composer.replying_to_yourself")
                  : t("composer.replying_to", { name: personName(t, replyTo!.sender) })}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {messageContentPreview(t, {
                ...(editing ?? replyTo!),
                attachmentCount: (editing ?? replyTo!).attachments.length,
              })}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t("composer.cancel")}
            onClick={() => {
              if (editing) setText("");
              setEditing(null);
              setReplyTo(null);
            }}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : null}

      {drafts.length ? (
        <div className="mb-2 flex gap-2 overflow-x-auto pb-1">
          {drafts.map((d) => (
            <div
              key={d.id}
              className="relative flex h-20 w-20 shrink-0 flex-col items-center justify-center overflow-hidden rounded-md border border-border bg-muted"
              title={d.file.name}
            >
              {d.previewUrl && fileKind(d.file) === "video" ? (
                <>
                  <video src={d.previewUrl} muted preload="metadata" className="size-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex size-7 items-center justify-center rounded-full bg-foreground/60 text-background">
                      <Play className="size-3.5 fill-current" />
                    </span>
                  </span>
                </>
              ) : d.previewUrl ? (
                <img src={d.previewUrl} alt="" className="size-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-1 px-1 text-center">
                  <FileText className="size-5 text-muted-foreground" />
                  <span className="w-full truncate text-[10px] text-foreground">{d.file.name}</span>
                  <span className="text-[9px] text-muted-foreground">{formatBytes(d.file.size)}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeDraft(d.id)}
                aria-label={t("composer.remove_attachment", { name: d.file.name })}
                className="absolute end-1 top-1 flex size-5 items-center justify-center rounded-full bg-foreground/70 text-background hover:bg-foreground"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
          {drafts.length < CHAT_MAX_ATTACHMENTS ? (
            <button
              type="button"
              // More of what the tray already holds.
              onClick={() =>
                (drafts[drafts.length - 1]?.asDocument ? fileInput : galleryInput).current?.click()
              }
              className="flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border-strong text-xs text-muted-foreground hover:bg-muted"
            >
              <Plus className="size-4" />
              {t("composer.add_more")}
            </button>
          ) : null}
        </div>
      ) : null}

      {compressing != null ? (
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          {t("composer.compressing", { percent: compressing })}
        </div>
      ) : null}

      {recording ? (
        <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface px-3 py-2 shadow-sm">
          <span className="size-2.5 animate-pulse rounded-full bg-danger" />
          <span className="text-sm font-medium tabular-nums text-foreground">
            {durationLabel(now - (recordStart ?? now))}
          </span>
          <span className="sr-only">{t("composer.recording")}</span>
          {/* Live meter: the last few seconds, newest on the inline-end side. */}
          <span className="flex h-7 flex-1 items-center justify-end gap-[3px] overflow-hidden" aria-hidden>
            {(levelsRef.current?.levels.slice(-48) ?? []).map((v, i) => (
              <span
                key={i}
                className="w-[3px] shrink-0 rounded-full bg-primary/70"
                style={{ height: `${Math.max(12, Math.round(v * 100))}%` }}
              />
            ))}
          </span>
          <Button variant="ghost" size="icon-sm" onClick={cancelRecording} aria-label={t("composer.cancel_recording")}>
            <Trash2 className="size-4 text-danger" />
          </Button>
          <Button variant="primary" size="icon-sm" onClick={() => void finishRecording()} aria-label={t("composer.stop_and_send")}>
            <SendHorizontal className="size-4 rtl-flip" />
          </Button>
        </div>
      ) : (
        <>
        {formatOpen ? <FormatToolbar onFormat={formatSelection} /> : null}
        <div
          className={cn(
            "relative flex items-end rounded-2xl border border-border bg-surface shadow-sm focus-within:border-primary",
            compact ? "gap-0.5 px-1 py-1" : "gap-2 px-2 py-1.5",
          )}
        >
          {suggesting ? (
            <MentionSuggestions
              id={mentionListId}
              people={candidates}
              note={isDirectChat ? t("mentions.direct_hint") : undefined}
              activeIndex={activeMention}
              onHover={setActiveMention}
              onPick={pickMention}
            />
          ) : null}
          <Popover open={attachOpen} onOpenChange={setAttachOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("composer.attach")}
                disabled={!!editing}
                className={cn("mb-0.5 shrink-0", iconBtn)}
              >
                {isLocating || room.isUploading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Paperclip className="size-4" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="w-80 p-3">
              <div className="grid grid-cols-3 gap-2">
                {attachItems.map(({ key, icon: Icon, tone, label, run }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setAttachOpen(false);
                      run();
                    }}
                    className="flex flex-col items-center gap-1.5 rounded-md p-2 text-xs text-foreground hover:bg-muted"
                  >
                    <span className={cn("flex size-10 items-center justify-center rounded-full", tone)}>
                      <Icon className="size-5" />
                    </span>
                    {label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Popover
            open={emojiOpen}
            onOpenChange={(open) => {
              setEmojiOpen(open);
              // Back to typing where the emoji went in.
              if (!open) requestAnimationFrame(() => textareaRef.current?.focus());
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("composer.emoji")}
                title={t("composer.emoji")}
                className={cn("mb-0.5 shrink-0", iconBtn)}
              >
                <Smile className="size-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="top"
              className="w-auto rounded-md! p-2"
              // Keep the caret: the textarea is what the emoji go into.
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {/* Stays open, like WhatsApp: pick several in a row. */}
              <EmojiPicker onPick={insertEmoji} />
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="icon-sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setFormatOpen((v) => !v)}
            aria-pressed={formatOpen}
            aria-label={t("format.toggle")}
            title={t("format.toggle")}
            className={cn("mb-0.5 shrink-0", iconBtn, formatOpen && "bg-primary/10 text-primary")}
          >
            <Type className="size-4" />
          </Button>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => {
              setCaret(e.target.selectionStart ?? e.target.value.length);
              onTextChange(e.target.value);
            }}
            // Caret moves (arrows, clicks) decide which "@" is being typed.
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
            onKeyDown={onKeyDown}
            aria-autocomplete={canMention ? "list" : undefined}
            aria-controls={suggesting ? mentionListId : undefined}
            aria-activedescendant={
              suggesting && candidates[activeMention]
                ? `${mentionListId}-${candidates[activeMention].id}`
                : undefined
            }
            onPaste={(e) => {
              const files = [...e.clipboardData.files];
              if (files.length && !editing) {
                e.preventDefault();
                addFiles(files);
              }
            }}
            rows={1}
            maxLength={CHAT_MAX_TEXT_LENGTH}
            placeholder={t("composer.placeholder")}
            aria-label={t("composer.placeholder")}
            className={cn(
              "field-sizing-content max-h-40 min-w-0 flex-1 resize-none bg-transparent text-sm text-foreground outline-hidden placeholder:text-muted-foreground",
              compact ? "min-h-8 px-1 py-1.5" : "min-h-9 px-1 py-2",
            )}
          />

          {canSubmit || editing ? (
            <div className={cn("mb-0.5 flex shrink-0 items-center", compact ? "gap-0.5" : "gap-1")}>
              {/* Schedule sits beside Send, quieter than it; right-clicking
                  Send opens it too. */}
              {canSchedule ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setScheduleOpen(true)}
                  disabled={busy}
                  aria-label={t("scheduled.schedule_message")}
                  title={t("scheduled.schedule_message")}
                  className={cn(
                    "animate-tab-fade-in rounded-full text-muted-foreground hover:bg-primary-muted hover:text-primary",
                    iconBtn,
                  )}
                >
                  <CalendarClock className="size-4" />
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="icon-sm"
                onClick={() => void submit()}
                onContextMenu={(e) => {
                  if (!canSchedule || busy) return;
                  e.preventDefault();
                  setScheduleOpen(true);
                }}
                disabled={!canSubmit || busy}
                aria-label={t("composer.send")}
                className={cn("rounded-full", iconBtn)}
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <SendHorizontal className="size-4 rtl-flip" />
                )}
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => void startRecording()}
              disabled={startingRecording || room.isUploading}
              aria-label={t("composer.record_voice")}
              title={t("composer.record_voice")}
              className={cn("mb-0.5 shrink-0 rounded-full", iconBtn)}
            >
              {/* Waiting for the mic prompt, or a just-recorded note uploading. */}
              {startingRecording || room.isUploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Mic className="size-4" />
              )}
            </Button>
          )}
        </div>
        </>
      )}

      <input
        ref={galleryInput}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(e) => {
          addFiles([...(e.target.files ?? [])]);
          e.target.value = "";
        }}
      />
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          addFiles([...(e.target.files ?? [])], "document");
          e.target.value = "";
        }}
      />
      <input
        ref={gifInput}
        type="file"
        accept="image/gif"
        multiple
        hidden
        onChange={(e) => {
          addFiles([...(e.target.files ?? [])]);
          e.target.value = "";
        }}
      />

      <CameraDialog
        open={sheet === "camera"}
        onClose={() => setSheet(null)}
        onCapture={(file) => addFiles([file])}
      />
      <PollDialog
        open={sheet === "poll"}
        onClose={() => setSheet(null)}
        onSubmit={(poll) =>
          void sendOther({ type: "poll", body: poll.question, poll }, { poll: { allowMultiple: !!poll.allowMultiple } })
        }
      />
      <LinkDialog
        open={sheet === "link"}
        onClose={() => setSheet(null)}
        onSubmit={(link) => void sendOther({ type: "link", link }, { link })}
      />
      <ShortcutDialog
        open={sheet === "shortcut"}
        onClose={() => setSheet(null)}
        myUserId={room.myUserId}
        onSubmit={({ shortcut, metadata }) =>
          void sendOther({ type: "shortcut", shortcut }, { shortcut: metadata })
        }
      />
      <TicketDialog
        open={sheet === "ticket"}
        onClose={() => setSheet(null)}
        myUserId={room.myUserId}
        onSubmit={({ shortcut, metadata }) =>
          void sendOther({ type: "shortcut", shortcut }, { shortcut: metadata })
        }
      />
      <ScheduleDialog
        open={scheduleOpen}
        isSubmitting={scheduling}
        onClose={() => setScheduleOpen(false)}
        onConfirm={(at) => void scheduleDraft(at)}
      />
      <PeoplePickerDialog
        open={sheet === "contact"}
        mode="single"
        title={t("contact_share.title")}
        excludeIds={room.myUserId != null ? [room.myUserId] : []}
        onClose={() => setSheet(null)}
        onConfirm={(people) => {
          if (people[0]) void shareContact(people[0]);
        }}
      />
    </div>
  );
});

/**
 * The "@" suggestions, above the composer from its inline-start edge. Rows
 * take the mouse without stealing the textarea's focus (and so its caret).
 */
const MentionSuggestions = ({
  id,
  people,
  activeIndex,
  onHover,
  onPick,
  note,
}: {
  id: string;
  people: IChatMember[];
  /** A line above the people (a direct chat: they won't be told). */
  note?: string;
  activeIndex: number;
  onHover: (index: number) => void;
  onPick: (person: IChatMember) => void;
}) => {
  const { t } = useTranslation("chat");
  const listRef = useRef<HTMLDivElement>(null);
  // Arrow keys move the highlight; keep it in view.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);
  return (
    <div
      ref={listRef}
      id={id}
      role="listbox"
      aria-label={t("mentions.suggestions")}
      className="absolute start-0 bottom-full z-20 mb-2 max-h-72 w-72 max-w-full animate-tab-fade-in overflow-y-auto rounded-xl border border-border bg-surface-elevated p-1 shadow-lg"
    >
      {note ? <p className="px-2 pt-1 pb-1.5 text-[11px] leading-snug text-muted-foreground">{note}</p> : null}
      {people.map((p, i) => {
        const name = mentionName(p);
        return (
          <div
            key={p.id}
            id={`${id}-${p.id}`}
            data-index={i}
            role="option"
            aria-selected={i === activeIndex}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => onHover(i)}
            onClick={() => onPick(p)}
            className={cn(
              "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-start",
              i === activeIndex ? "bg-primary-muted" : "hover:bg-muted",
            )}
          >
            <ChatAvatar name={name} imageUrl={p.profileImage} size="sm" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" dir="auto">
              {name}
            </span>
            {p.role !== "member" ? (
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {p.role === "owner" ? t("info.role_owner") : t("info.role_admin")}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
